import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Phone, Users, Mail, MessageSquare, Video, MapPin, FileText,
  StickyNote, Activity, Plus, Clock, Calendar, Loader2, Check,
  ChevronDown, MoreVertical, Edit2, Trash2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_ICONS = {
  call: Phone,
  meeting: Users,
  email: Mail,
  linkedin: MessageSquare,
  sms: MessageSquare,
  video_call: Video,
  site_visit: MapPin,
  document: FileText,
  note: StickyNote,
  other: Activity
};

const ACTIVITY_COLORS = {
  call: 'bg-green-100 text-green-700 border-green-200',
  meeting: 'bg-purple-100 text-purple-700 border-purple-200',
  email: 'bg-blue-100 text-blue-700 border-blue-200',
  linkedin: 'bg-[#0A66C2]/10 text-[#0A66C2] border-[#0A66C2]/20',
  sms: 'bg-amber-100 text-amber-700 border-amber-200',
  video_call: 'bg-pink-100 text-pink-700 border-pink-200',
  site_visit: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  document: 'bg-purple-100 text-purple-700 border-purple-200',
  note: 'bg-gray-100 text-gray-700 border-gray-200',
  other: 'bg-gray-100 text-gray-700 border-gray-200'
};

function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

// Activity Timeline Component
export function ActivityTimeline({ leadId, activities, onRefresh }) {
  const { token } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  
  if (!activities || activities.length === 0) {
    return (
      <div className="text-center py-8">
        <Activity className="w-10 h-10 text-[#8C8078] mx-auto mb-2" />
        <p className="text-[#5D524D] text-sm mb-3">No activities logged yet</p>
        <Button
          size="sm"
          onClick={() => setShowAddModal(true)}
          className="bg-[#4A3B32] hover:bg-[#3A2B22]"
        >
          <Plus className="w-4 h-4 mr-1" />
          Log Activity
        </Button>
        
        <LogActivityModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          leadId={leadId}
          onSuccess={onRefresh}
        />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-[#2C2420]">Activity Timeline</h4>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddModal(true)}
        >
          <Plus className="w-4 h-4 mr-1" />
          Log Activity
        </Button>
      </div>
      
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#E6E0D9]" />
        
        {/* Activity items */}
        <div className="space-y-4">
          {activities.map((activity) => {
            const Icon = ACTIVITY_ICONS[activity.type] || Activity;
            const colorClass = ACTIVITY_COLORS[activity.type] || ACTIVITY_COLORS.other;
            
            return (
              <div key={activity.id} className="relative flex gap-3 pl-2">
                {/* Icon */}
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center border z-10",
                  colorClass
                )}>
                  <Icon className="w-4 h-4" />
                </div>
                
                {/* Content */}
                <div className="flex-1 bg-white rounded-lg border border-[#E6E0D9] p-3 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-[#2C2420] text-sm">{activity.title}</p>
                      <p className="text-xs text-[#8C8078] mt-0.5">
                        {activity.loggedBy} • {formatDate(activity.activityDate)}
                      </p>
                    </div>
                    {activity.duration && (
                      <span className="text-xs text-[#5D524D] flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {activity.duration}m
                      </span>
                    )}
                  </div>
                  
                  {activity.description && (
                    <p className="text-sm text-[#5D524D] mt-2">{activity.description}</p>
                  )}
                  
                  {activity.outcome && (
                    <span className={cn(
                      "inline-block text-xs px-2 py-0.5 rounded mt-2",
                      activity.outcome.includes('Positive') ? 'bg-green-100 text-green-700' :
                      activity.outcome.includes('Negative') ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    )}>
                      {activity.outcome}
                    </span>
                  )}
                  
                  {activity.nextSteps && (
                    <div className="mt-2 p-2 bg-[#F9F7F5] rounded text-xs text-[#5D524D]">
                      <span className="font-medium">Next: </span>{activity.nextSteps}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <LogActivityModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        leadId={leadId}
        onSuccess={onRefresh}
      />
    </div>
  );
}

// Log Activity Modal
export function LogActivityModal({ isOpen, onClose, leadId, onSuccess }) {
  const { token } = useAuth();
  const [activityTypes, setActivityTypes] = useState([]);
  const [callOutcomes, setCallOutcomes] = useState([]);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    type: 'call',
    title: '',
    description: '',
    outcome: '',
    duration: '',
    meetingType: '',
    nextSteps: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchOptions();
    }
  }, [isOpen]);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/leads/activities/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityTypes(response.data.types || []);
      setCallOutcomes(response.data.callOutcomes || []);
      setMeetingTypes(response.data.meetingTypes || []);
    } catch (error) {
      console.error('Failed to fetch activity options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/leads/activities`, {
        leadId,
        type: formData.type,
        title: formData.title,
        description: formData.description || null,
        outcome: formData.outcome || null,
        duration: formData.duration ? parseInt(formData.duration) : null,
        meetingType: formData.meetingType || null,
        nextSteps: formData.nextSteps || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Activity logged');
      onSuccess?.();
      onClose();
      setFormData({
        type: 'call',
        title: '',
        description: '',
        outcome: '',
        duration: '',
        meetingType: '',
        nextSteps: ''
      });
    } catch (error) {
      toast.error('Failed to log activity');
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-generate title based on type and outcome
  useEffect(() => {
    if (formData.type === 'call' && formData.outcome) {
      setFormData(prev => ({
        ...prev,
        title: `Phone Call - ${formData.outcome}`
      }));
    } else if (formData.type === 'meeting' && formData.meetingType) {
      setFormData(prev => ({
        ...prev,
        title: formData.meetingType
      }));
    }
  }, [formData.type, formData.outcome, formData.meetingType]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log Activity</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#4A3B32]" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Activity Type */}
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <div className="grid grid-cols-5 gap-2">
                {activityTypes.slice(0, 5).map((type) => {
                  const Icon = ACTIVITY_ICONS[type.id] || Activity;
                  const isSelected = formData.type === type.id;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
                      className={cn(
                        "p-2 rounded-lg border flex flex-col items-center gap-1 transition-all",
                        isSelected
                          ? "border-[#4A3B32] bg-[#4A3B32]/5"
                          : "border-[#E6E0D9] hover:border-[#C6A88F]"
                      )}
                    >
                      <Icon className={cn("w-5 h-5", isSelected ? "text-[#4A3B32]" : "text-[#8C8078]")} />
                      <span className="text-xs text-[#5D524D]">{type.name.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Call-specific fields */}
            {formData.type === 'call' && (
              <div className="space-y-2">
                <Label>Outcome</Label>
                <Select
                  value={formData.outcome}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, outcome: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    {callOutcomes.map((outcome) => (
                      <SelectItem key={outcome} value={outcome}>{outcome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Meeting-specific fields */}
            {formData.type === 'meeting' && (
              <div className="space-y-2">
                <Label>Meeting Type</Label>
                <Select
                  value={formData.meetingType}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, meetingType: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select meeting type" />
                  </SelectTrigger>
                  <SelectContent>
                    {meetingTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Title */}
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Activity title"
              />
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                placeholder="e.g. 30"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add any notes..."
                className="min-h-[80px]"
              />
            </div>

            {/* Next Steps */}
            <div className="space-y-2">
              <Label>Next Steps</Label>
              <Input
                value={formData.nextSteps}
                onChange={(e) => setFormData(prev => ({ ...prev, nextSteps: e.target.value }))}
                placeholder="What needs to happen next?"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#4A3B32] hover:bg-[#3A2B22]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Activity'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ActivityTimeline;
